<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { API, HOST } from '@/utils/http'
import { RefreshCw, Send } from 'lucide-vue-next'

interface EmailTemplate {
  key: string
  label: string
}

const store = useStore()
const member = computed(() => store.getters.getMember)

const templates = ref<EmailTemplate[]>([])
const selectedKey = ref<string | null>(null)
const previewUrl = ref<string | null>(null)
const error = ref<string | null>(null)
const refreshing = ref(false)
const sending = ref(false)
const sendFeedback = ref<string | null>(null)

const sendEmail = ref('')

watch(member, (m) => {
  if (m?.email && !sendEmail.value) sendEmail.value = m.email
}, { immediate: true })
let currentBlobUrl: string | null = null

function onIframeLoad(e: Event) {
  const iframe = e.target as HTMLIFrameElement
  try {
    const height = iframe.contentDocument?.body?.scrollHeight
    if (height) iframe.style.height = height + 'px'
  } catch {}
}

onMounted(async () => {
  try {
    const res = await API.get(`${HOST}/admin/emails`)
    templates.value = res.data
    if (templates.value.length) select(templates.value[0])
  } catch (e: any) {
    error.value = e.message
  }
})

async function loadPreview(key: string) {
  const params = sendEmail.value ? { to: sendEmail.value } : {}
  const res = await API.get(`${HOST}/admin/emails/preview/${key}`, { responseType: 'text', params })
  if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl)
  const blob = new Blob([res.data], { type: 'text/html' })
  currentBlobUrl = URL.createObjectURL(blob)
  previewUrl.value = currentBlobUrl
}

async function select(template: EmailTemplate) {
  selectedKey.value = template.key
  sendFeedback.value = null
  error.value = null
  try {
    await loadPreview(template.key)
  } catch (e: any) {
    error.value = e.message
  }
}

async function refresh() {
  if (!selectedKey.value || refreshing.value) return
  refreshing.value = true
  error.value = null
  try {
    await loadPreview(selectedKey.value)
  } catch (e: any) {
    error.value = e.message
  } finally {
    refreshing.value = false
  }
}

async function sendTest() {
  if (!selectedKey.value || sending.value) return
  const to = sendEmail.value.trim()
  if (!to) return
  sending.value = true
  sendFeedback.value = null
  try {
    const res = await API.post(`${HOST}/admin/emails/send-preview/${selectedKey.value}`, null, { params: { to } })
    sendFeedback.value = res.data.detail
  } catch (e: any) {
    sendFeedback.value = e.response?.data?.detail || e.message
  } finally {
    sending.value = false
  }
}

const canSend = computed(() => !!selectedKey.value && !sending.value && sendEmail.value.trim().length > 0)
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />

    <div class="flex flex-1 overflow-hidden">
      <!-- Sidebar -->
      <aside class="w-56 shrink-0 border-r bg-muted/50 overflow-y-auto">
        <div class="p-3 space-y-1">
          <p class="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">Templates</p>
          <Button
            v-for="t in templates"
            :key="t.key"
            :variant="selectedKey === t.key ? 'default' : 'ghost'"
            size="sm"
            class="w-full justify-start"
            @click="select(t)"
          >
            {{ t.label }}
          </Button>
        </div>
      </aside>

      <!-- Main -->
      <div class="flex-1 flex flex-col overflow-hidden">

        <!-- Toolbar -->
        <div class="flex items-center gap-2 px-4 py-2 border-b bg-background">
          <span class="text-sm text-muted-foreground">Send to:</span>
          <Input
            v-model="sendEmail"
            placeholder="email@example.com"
            class="h-8 w-56 text-sm"
            tabindex="1"
            @keydown.enter="refresh"
          />
          <Button variant="outline" size="sm" :disabled="!selectedKey || refreshing" tabindex="2" @click="refresh">
            <RefreshCw class="h-4 w-4 mr-2" :class="{ 'animate-spin': refreshing }" />
            Refresh
          </Button>
          <Button size="sm" :disabled="!canSend" tabindex="3" @click="sendTest">
            <Send class="h-4 w-4 mr-2" />
            {{ sending ? 'Sending…' : 'Send Test' }}
          </Button>
          <span v-if="sendFeedback" class="text-sm text-muted-foreground">{{ sendFeedback }}</span>
        </div>

        <!-- Preview -->
        <main class="flex-1 overflow-auto bg-muted flex justify-center py-6">
          <div v-if="error" class="text-destructive">{{ error }}</div>
          <iframe
            v-else-if="previewUrl"
            :src="previewUrl"
            class="border-0 shrink-0"
            style="width:600px; height:800px;"
            sandbox="allow-same-origin"
            :key="previewUrl"
            @load="onIframeLoad"
          />
        </main>

      </div>
    </div>
  </div>
</template>
